import React, { useState, useEffect } from 'react';
import { X, CreditCard, Banknote, Smartphone, DollarSign } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';
import type { PaymentMethod } from '../../types/pharmacy-pos';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  grandTotal: number;
  onConfirmPayment: (paymentMethod: PaymentMethod, amountReceived: number) => void;
  loading?: boolean;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  grandTotal,
  onConfirmPayment,
  loading = false
}) => {
  const { formatCurrency, currencySymbol } = useCurrency();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [amountReceived, setAmountReceived] = useState<string>(grandTotal.toFixed(2));
  const [changeDue, setChangeDue] = useState<number>(0);

  useEffect(() => {
    if (isOpen) {
      setAmountReceived(grandTotal.toFixed(2));
      setPaymentMethod('cash');
    }
  }, [isOpen, grandTotal]);

  useEffect(() => {
    const received = parseFloat(amountReceived) || 0;
    const change = received - grandTotal;
    setChangeDue(change > 0 ? change : 0);
  }, [amountReceived, grandTotal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const received = parseFloat(amountReceived) || 0;
    
    if (received < grandTotal) {
      alert('Amount received cannot be less than grand total');
      return;
    }

    onConfirmPayment(paymentMethod, received);
  };

  const paymentMethods: Array<{ value: PaymentMethod; label: string; icon: React.ReactNode }> = [
    { value: 'cash', label: 'Cash', icon: <Banknote className="w-5 h-5" /> },
    { value: 'card', label: 'Card', icon: <CreditCard className="w-5 h-5" /> },
    { value: 'mfs', label: 'MFS (bKash/Nagad)', icon: <Smartphone className="w-5 h-5" /> },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: <DollarSign className="w-5 h-5" /> }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Payment</h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Grand Total Display */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-1">Grand Total</div>
            <div className="text-3xl font-bold text-green-600">{formatCurrency(grandTotal)}</div>
          </div>

          {/* Payment Method Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {paymentMethods.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setPaymentMethod(method.value)}
                  disabled={loading}
                  className={`
                    flex items-center justify-center space-x-2 px-4 py-3 rounded-lg border-2 transition-all
                    ${paymentMethod === method.value
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  {method.icon}
                  <span className="text-sm font-medium">{method.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Amount Received */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount Received <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">{currencySymbol}</span>
              <input
                type="number"
                step="0.01"
                min={grandTotal}
                value={amountReceived}
                onChange={(e) => setAmountReceived(e.target.value)}
                disabled={loading}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
                required
              />
            </div>
          </div>

          {/* Change Due */}
          {changeDue > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-blue-900">Change Due:</span>
                <span className="text-lg font-bold text-blue-600">{formatCurrency(changeDue)}</span>
              </div>
            </div>
          )}

          {/* Quick Amount Buttons (for Cash only) */}
          {paymentMethod === 'cash' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quick Amount</label>
              <div className="grid grid-cols-4 gap-2">
                {[100, 200, 500, 1000].map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setAmountReceived((grandTotal + amount).toFixed(2))}
                    disabled={loading}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 disabled:opacity-50"
                  >
                    +{currencySymbol}{amount}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || parseFloat(amountReceived) < grandTotal}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
            >
              {loading ? 'Processing...' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
